import { useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button as Button } from '@factorialco/f0-react'
import { EntityId, EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'
import { WorkflowRoleSelectorDocument } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import JobCatalogRoleSelector from './JobCatalogRoleSelector'
import LegacyRoleSelector from './LegacyRoleSelector'
import {
  StrategySelector,
  type StrategySelectorEmployee,
  type StrategySelectorTeam,
} from './StrategySelector'
import { type StrategyItem, getStrategyOptions, getStrategyType } from './manualApproveHandlerUtils'

interface ApprovalSettingsModalProps {
  isOpen: boolean
  onClose: () => void
  onAddStrategies: (strategies: StrategyItem[]) => void
  availableEmployees: StrategySelectorEmployee[]
  availableTeams: StrategySelectorTeam[]
  employeesLoading: boolean
  teamsLoading: boolean
  existingStrategies: StrategyItem[]
}

export const ApprovalSettingsModal = ({
  isOpen,
  onClose,
  onAddStrategies,
  availableEmployees,
  availableTeams,
  employeesLoading,
  teamsLoading,
  existingStrategies,
}: ApprovalSettingsModalProps) => {
  const [strategy, setStrategy] = useState<string>('')
  const [selectedEmployeesIds, setSelectedEmployeesIds] = useState<EntityId[]>([])
  const [selectedTeamsIds, setSelectedTeamsIds] = useState<EntityId[]>([])
  const [selectedRoleId, setSelectedRoleId] = useState<string | undefined>(undefined)

  const { company } = useCurrentSession()

  const { data: rolesData } = useQuery(WorkflowRoleSelectorDocument, {
    variables: { companyId: company.id },
  })

  const isLegacyJobCatalog =
    rolesData?.features?.featuresConnection?.nodes?.find(
      (feature) => feature.name === 'legacy_job_catalog'
    )?.enabled ?? true

  const availableRoles = rolesData?.jobCatalog?.rolesConnection?.nodes || []
  const treeRoles = rolesData?.jobCatalog?.treeNodesConnection?.nodes || []

  if (!isOpen) return null

  const resetModal = () => {
    setStrategy('')
    setSelectedEmployeesIds([])
    setSelectedTeamsIds([])
    setSelectedRoleId(undefined)
  }

  const handleClose = () => {
    resetModal()
    onClose()
  }

  const handleSave = () => {
    if (!strategy) {
      return
    }

    const strategyType = getStrategyType(strategy)
    const strategiesToAdd: StrategyItem[] = []

    if (strategyType === 'manual_selected_employees' && selectedEmployeesIds.length > 0) {
      selectedEmployeesIds.forEach((employeeId) => {
        if (typeof employeeId === 'number') {
          strategiesToAdd.push({
            id: `manual_selection_${employeeId}`,
            type: 'manual_selection',
            employeeId: employeeId,
          })
        }
      })
    } else if (strategyType === 'team' && selectedTeamsIds.length > 0) {
      const teamId = selectedTeamsIds[0]
      if (typeof teamId === 'number') {
        strategiesToAdd.push({
          id: `${strategy}_${teamId}_${Date.now()}`,
          type: strategy,
          teamId: teamId,
        })
      }
    } else if (strategyType === 'role' && selectedRoleId) {
      strategiesToAdd.push({
        id: `${strategy}_${selectedRoleId}_${Date.now()}`,
        type: strategy,
        roleId: selectedRoleId,
      })
    } else if (strategyType === 'none') {
      strategiesToAdd.push({
        id: `${strategy}_${Date.now()}`,
        type: strategy,
      })
    }

    if (strategiesToAdd.length > 0) {
      onAddStrategies(strategiesToAdd)
    }

    resetModal()
    onClose()
  }

  // Filter out already-selected employees
  const selectedEmployeeIds = existingStrategies
    .filter((s) => s.type === 'manual_selection' && s.employeeId !== undefined)
    .map((s) => s.employeeId)
    .filter((id): id is number => id !== undefined)

  const filteredEmployees = availableEmployees.filter(
    (emp) => !selectedEmployeeIds.includes(emp.id)
  )

  // Filter out already-used teams for the selected strategy type
  const getFilteredTeamsForStrategy = (strategyType: string) => {
    const usedTeamIdsForThisStrategy = existingStrategies
      .filter((s) => s.type === strategyType && s.teamId !== undefined)
      .map((s) => s.teamId)
      .filter((id): id is number => id !== undefined)

    return availableTeams.filter((team) => !usedTeamIdsForThisStrategy.includes(team.id))
  }

  // Build selected entities for display
  const selectedEmployeesEntities = availableEmployees
    .filter((emp) => selectedEmployeesIds.includes(emp.id))
    .map((emp) => ({
      id: emp.id,
      name: emp.fullName,
      avatar: buildUrl(emp.avatar?.url),
    }))

  const selectedTeamsEntities = availableTeams
    .filter((team) => selectedTeamsIds.includes(team.id))
    .map((team) => ({
      id: team.id,
      name: team.name,
    }))

  const strategyType = getStrategyType(strategy)

  const isSaveDisabled = (() => {
    if (!strategy) return true
    if (strategyType === 'none') return false
    if (strategyType === 'manual_selected_employees') return selectedEmployeesIds.length === 0
    if (strategyType === 'team') return selectedTeamsIds.length === 0
    if (strategyType === 'role') return !selectedRoleId
    return false
  })()

  const filteredTeams = strategyType === 'team' ? getFilteredTeamsForStrategy(strategy) : []

  // Filter out already-used 'none' type strategies from options
  const usedNoneTypeStrategies = existingStrategies
    .filter((s) => getStrategyType(s.type) === 'none')
    .map((s) => s.type)

  const filteredStrategyOptions = getStrategyOptions().filter((option) => {
    if (option.ui_type === 'none') {
      return !usedNoneTypeStrategies.includes(option.value)
    }
    return true
  })

  const handleEmployeeSelect = (selection: EntitySelectEntity[] | EntitySelectEntity | null) => {
    if (selection === null) {
      setSelectedEmployeesIds([])
      return
    }
    setSelectedEmployeesIds(Array.isArray(selection) ? selection.map((s) => s.id) : [selection.id])
  }

  const handleTeamSelect = (selection: EntitySelectEntity[] | EntitySelectEntity | null) => {
    if (selection === null) {
      setSelectedTeamsIds([])
      return
    }
    const teamId = Array.isArray(selection) ? selection[0]?.id : selection.id
    setSelectedTeamsIds(teamId ? [teamId] : [])
  }

  return (
    <Modal
      disableFocusTrap
      title={i18n.t('workflows.sidepanel.approval.add_approvers.title')}
      footer={
        <>
          <Button
            label={i18n.t('common.cancel')}
            variant="outline"
            size="md"
            onClick={handleClose}
          />
          <Button
            label={i18n.t('common.save_changes')}
            variant="default"
            size="md"
            onClick={handleSave}
            disabled={isSaveDisabled}
          />
        </>
      }
      onClose={handleClose}
    >
      <Box gap="s16">
        <Box gap="s8" width="full">
          <Text size="200" weight="medium" className="text-f1-foreground-secondary">
            {i18n.t('workflows.sidepanel.approval.fields.add_title')}
          </Text>

          <StrategySelector
            strategyValue={strategy}
            onStrategyChange={setStrategy}
            onEmployeeSelect={handleEmployeeSelect}
            onTeamSelect={handleTeamSelect}
            selectedEmployees={selectedEmployeesEntities}
            selectedTeams={selectedTeamsEntities}
            availableEmployees={filteredEmployees}
            availableTeams={filteredTeams}
            employeesLoading={employeesLoading}
            teamsLoading={teamsLoading}
            singleEmployeeSelection={false}
            strategyOptions={filteredStrategyOptions}
          />

          {strategyType === 'role' && isLegacyJobCatalog && (
            <LegacyRoleSelector
              availableRoles={availableRoles}
              value={selectedRoleId}
              onChange={(id) => setSelectedRoleId(id ?? undefined)}
            />
          )}

          {strategyType === 'role' && !isLegacyJobCatalog && (
            <JobCatalogRoleSelector
              treeRoles={treeRoles}
              value={selectedRoleId}
              onChange={(uuid) => {
                setSelectedRoleId(uuid ?? undefined)
              }}
            />
          )}
        </Box>
      </Box>
    </Modal>
  )
}
